"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Car, Eye, EyeOff, Loader2, Store, CheckCircle } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

export default function RegisterPage() {
    const router = useRouter();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const [form, setForm] = useState({
        full_name: "",
        email: "",
        password: "",
        phone: "",
        role: "vendor",
        // Store info
        store_name: "",
        store_name_ar: "",
        vendor_type: "parts_store",
        // Bank info
        bank_name: "",
        bank_account_name: "",
        bank_iban: "",
    });

    const [files, setFiles] = useState<{ commercial_register: File | null, business_license: File | null }>({
        commercial_register: null,
        business_license: null
    });

    const set = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));
    const setFile = (key: 'commercial_register' | 'business_license', file: File | null) => setFiles((prev) => ({ ...prev, [key]: file }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const formData = new FormData();
            Object.entries(form).forEach(([k, v]) => formData.append(k, v));
            if (files.commercial_register) formData.append("commercial_register", files.commercial_register);
            if (files.business_license) formData.append("business_license", files.business_license);

            const res = await fetch(`${API}/api/auth/register/`, {
                method: "POST",
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) {
                const msg = data.email?.[0] || data.detail || (Object.values(data)[0] as any)?.[0] || "حدث خطأ. حاول مجدداً.";
                setError(msg as string);
                return;
            }
            // Store tokens
            localStorage.setItem("s360_access", data.access);
            localStorage.setItem("s360_refresh", data.refresh);
            setStep(4); // Success screen
            setTimeout(() => router.push("/profile"), 3000);
        } catch {
            setError("خطأ في الاتصال بالخادم.");
        } finally {
            setLoading(false);
        }
    };

    if (step === 4) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-green-950 to-slate-900 flex items-center justify-center p-4">
                <div className="text-center">
                    <CheckCircle className="h-24 w-24 text-green-400 mx-auto mb-6 animate-bounce" />
                    <h2 className="text-2xl font-black text-white mb-2">تم التسجيل بنجاح!</h2>
                    <p className="text-green-300">يتم إعادة توجيهك إلى لوحة التحكم...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-center justify-center p-4">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/3 -left-24 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/3 -right-24 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-lg">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-500 rounded-3xl shadow-2xl shadow-orange-900/50 mb-4">
                        <Store className="h-10 w-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-black text-white">تسجيل تاجر جديد</h1>
                    <p className="text-orange-300 text-sm mt-1">أنشئ متجرك على منصة سيارتي 360</p>
                </div>

                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                    {/* Step tabs */}
                    <div className="flex gap-2 mb-6">
                        {[1, 2, 3].map((s) => (
                            <div
                                key={s}
                                className={`flex-1 h-1.5 rounded-full transition-all ${step >= s ? "bg-orange-500" : "bg-white/10"}`}
                            />
                        ))}
                    </div>

                    <form onSubmit={step < 3 ? (e) => { e.preventDefault(); setStep(s => s + 1); } : handleSubmit} className="space-y-4">
                        {step === 1 && (
                            <>
                                <h3 className="text-white font-bold mb-3">المعلومات الشخصية</h3>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">الاسم الكامل</label>
                                    <input type="text" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" placeholder="محمد أحمد" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">البريد الإلكتروني</label>
                                    <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required dir="ltr"
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" placeholder="vendor@email.com" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">رقم الهاتف</label>
                                    <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} dir="ltr"
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" placeholder="+966501234567" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">كلمة المرور</label>
                                    <div className="relative">
                                        <input type={showPassword ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)} required dir="ltr"
                                            className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition pr-12" placeholder="••••••••" />
                                        <button type="button" onClick={() => setShowPassword((v) => !v)}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition">
                                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <h3 className="text-white font-bold mb-3">معلومات المنشأة</h3>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">اسم المتجر (بالإنجليزية)</label>
                                    <input type="text" value={form.store_name} onChange={(e) => set("store_name", e.target.value)} dir="ltr"
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" placeholder="Elite Auto Parts" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">اسم المتجر (بالعربية)</label>
                                    <input type="text" value={form.store_name_ar} onChange={(e) => set("store_name_ar", e.target.value)}
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" placeholder="معرض النخبة لقطع الغيار" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-3">نوع المنشأة</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {([
                                            { value: 'parts_store', emoji: '🔩', label: 'متجر قطع غيار', highlight: false },
                                            { value: 'workshop',    emoji: '🔧', label: 'ورشة سيارات',  highlight: true  },
                                            { value: 'car_seller', emoji: '🚗', label: 'معرض سيارات',  highlight: false },
                                            { value: 'rental',     emoji: '🚙', label: 'تأجير سيارات', highlight: false },
                                        ] as const).map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => set('vendor_type', opt.value)}
                                                className={`relative flex flex-col items-center gap-1 py-3 px-2 rounded-2xl border-2 text-sm font-bold transition-all ${
                                                    form.vendor_type === opt.value
                                                        ? opt.highlight
                                                            ? 'border-orange-400 bg-orange-500/30 text-white shadow-lg shadow-orange-900/30'
                                                            : 'border-orange-400 bg-orange-500/20 text-white'
                                                        : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                                                }`}
                                            >
                                                {opt.highlight && (
                                                    <span className="absolute -top-2 -right-1 bg-orange-400 text-white text-[9px] font-black px-2 py-0.5 rounded-full">متعدد البائعين</span>
                                                )}
                                                <span className="text-2xl">{opt.emoji}</span>
                                                <span>{opt.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 3 && (
                            <>
                                <h3 className="text-white font-bold mb-3">المستندات والبنك</h3>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">السجل التجاري (ملف)</label>
                                    <input type="file" onChange={(e) => setFile("commercial_register", e.target.files?.[0] || null)}
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">رخصة البلدية / العمل (ملف)</label>
                                    <input type="file" onChange={(e) => setFile("business_license", e.target.files?.[0] || null)}
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" />
                                </div>
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">اسم البنك</label>
                                    <input type="text" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)}
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" placeholder="مثال: البنك الأهلي" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">اسم صاحب الحساب المستفيد</label>
                                    <input type="text" value={form.bank_account_name} onChange={(e) => set("bank_account_name", e.target.value)}
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" placeholder="مثال: مؤسسة النخبة" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-orange-200 mb-1.5">رقم الآيبان IBAN</label>
                                    <input type="text" value={form.bank_iban} onChange={(e) => set("bank_iban", e.target.value)} dir="ltr"
                                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition" placeholder="SA..." />
                                </div>
                            </>
                        )}

                        {error && (
                            <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 text-red-300 text-sm text-center">
                                {error}
                            </div>
                        )}

                        <div className="flex gap-3">
                            {step > 1 && (
                                <button type="button" onClick={() => setStep(s => s - 1)}
                                    className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold rounded-2xl py-3.5 text-sm transition">
                                    السابق
                                </button>
                            )}
                            <button type="submit" disabled={loading}
                                className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-800 disabled:cursor-not-allowed text-white font-bold rounded-2xl py-3.5 text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-orange-900/50">
                                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                                {step < 3 ? "التالي" : loading ? "جاري التسجيل..." : "إنشاء الحساب"}
                            </button>
                        </div>
                    </form>

                    <p className="text-center text-white/40 text-xs mt-4">
                        لديك حساب؟{" "}
                        <Link href="/login" className="text-orange-400 hover:underline">تسجيل الدخول</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
